import { all, call, getContext, put, takeLeading } from 'redux-saga/effects';

import type { ConfirmedChange } from '../domain/confirm-email-change.model';
import { toConfirmEmailChangeError } from '../domain/confirm-email-change-error';
import type { ConfirmEmailChangeGateway } from '../infrastructure/confirm-email-change.gateway';
import { confirmRequested } from './confirm-email-change.actions';
import { confirmEmailChangeActions } from './confirm-email-change.slice';

/**
 * WORKER: commit the pending email change from the token in the link.
 *
 * No validation step: there is nothing to validate. The token is either accepted by
 * the server or it is not, and this side cannot tell the difference in advance.
 */
function* confirmWorker(action: ReturnType<typeof confirmRequested>) {
  const gateway: ConfirmEmailChangeGateway = yield getContext('confirmEmailChangeGateway');
  yield put(confirmEmailChangeActions.confirmStarted());
  try {
    const confirmed = (yield call(gateway.confirm, action.payload.token)) as ConfirmedChange;
    yield put(confirmEmailChangeActions.confirmSucceeded(confirmed));
  } catch (e) {
    yield put(confirmEmailChangeActions.confirmFailed(toConfirmEmailChangeError(e)));
  }
}

/**
 * WATCHERS.
 *
 * `takeLeading`, not `takeLatest`. This commit is single-use: the first call consumes
 * the token, so a second in-flight call would be answered 401 and would overwrite a
 * success with a failure; telling the visitor their link was invalid immediately
 * after it worked. React 18's double-invoked mount effects in development make that
 * a certainty rather than a race worth ignoring.
 */
export function* confirmEmailChangeSaga() {
  yield all([takeLeading(confirmRequested.type, confirmWorker)]);
}
